import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { OrgSwitcher } from "@/components/org-switcher";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/templates")({ component: TemplatesPage });

const CHANNELS = ["sms", "ussd", "whatsapp", "email", "voice", "push"] as const;

function TemplatesPage() {
  const { orgId } = useCurrentOrg();
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", channel: "sms" as (typeof CHANNELS)[number], subject: "", body: "" });

  async function load() {
    if (!orgId) return;
    const { data } = await supabase.from("message_templates").select("*")
      .eq("organization_id", orgId).order("created_at", { ascending: false });
    setRows(data ?? []);
  }
  useEffect(() => { load(); }, [orgId]);

  async function save() {
    if (!orgId) return;
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("message_templates").insert({
      organization_id: orgId, name: form.name, channel: form.channel,
      subject: form.subject || null, body: form.body, created_by: u.user?.id,
    });
    if (error) return toast.error(error.message);
    toast.success("Template saved");
    setOpen(false); setForm({ name: "", channel: "sms", subject: "", body: "" });
    load();
  }

  async function remove(id: string) {
    const { error } = await supabase.from("message_templates").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Templates</h1>
          <p className="text-muted-foreground text-sm mt-1">Reusable message bodies. Use <code className="text-xs bg-muted px-1 rounded">{"{{name}}"}</code> for variables.</p>
        </div>
        <div className="flex gap-2">
          <OrgSwitcher />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New template</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create template</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div>
                  <Label>Channel</Label>
                  <Select value={form.channel} onValueChange={(v: any) => setForm({ ...form, channel: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.channel === "email" && (
                  <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
                )}
                <div>
                  <Label>Body</Label>
                  <Textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} maxLength={2000} />
                </div>
                <Button onClick={save} className="w-full" disabled={!form.name || !form.body}>Save template</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Channel</TableHead><TableHead>Body preview</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell><Badge variant="outline">{t.channel.toUpperCase()}</Badge></TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-md truncate">{t.body}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => remove(t.id)}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-12">No templates yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
